// Tokenized, responsive CRM primary navigation.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import "./Sidebar.css";
import { NavLink, useLocation } from "react-router-dom";
import { apiClient, SESSION_CHANGED_EVENT } from "../pages/backend_url";
import { resolveModuleRoute } from "../navigation/moduleRoutes";
import { isTemporarilyHiddenModule } from "../navigation/moduleVisibility";
import { useLanguage } from "../../localization/LanguageContext";
import {
  resolveWorkspaceDisplayName,
  SHELL_NAVIGATION_COLLAPSED_KEY,
  SHELL_DRAWER_MEDIA_QUERY,
} from "./shellPolicy";

import {
  Archive as ArchiveRoundedIcon,
  Boxes as Inventory2RoundedIcon,
  Building2 as BusinessRoundedIcon,
  ChartNoAxesCombined as QueryStatsIcon,
  ChevronsLeft as KeyboardDoubleArrowLeftRoundedIcon,
  ChevronsRight as KeyboardDoubleArrowRightRoundedIcon,
  CircleAlert as ErrorOutlineRoundedIcon,
  ClipboardCheck as FactCheckRoundedIcon,
  ContactRound as ContactPageIcon,
  Headset as SupportAgentRoundedIcon,
  History as HistoryRoundedIcon,
  Inbox as InboxRoundedIcon,
  LayoutDashboard as DashboardIcon,
  Mail as EmailIcon,
  MapPin as LocationOnRoundedIcon,
  Network as HubRoundedIcon,
  PanelsTopLeft as AppsRoundedIcon,
  Search as SearchIcon,
  SearchCheck as PersonSearchIcon,
  Settings2 as ManageAccountsRoundedIcon,
  Shield as ShieldRoundedIcon,
  SlidersHorizontal as TuneRoundedIcon,
  Sparkles as AutoAwesomeMotionRoundedIcon,
  Star as StarIcon,
  StarOff as StarBorderIcon,
  X as ClearRoundedIcon,
} from "lucide-react";

// Recent work is intentionally session-local and stores only static product
// routes. It never persists record names, customer data, filters or a ticket
// URL that could reveal the work a person was performing.
const RECENT_WORK_STORAGE_KEY = "crm_sidebar_recent_work.v1";
const RECENT_WORK_LIMIT = 6;
const ALLOWED_TABS = ["all", "fav", "recent"];
// Keep the rail's readable content in place for the first part of its width
// transition. Without this small presentation phase, React removes labels at
// the same instant that the work canvas starts moving, which feels like a hard
// visual push even though the rail itself is animated.
// The shell owns rail geometry for 360ms. Changing the desktop content plane
// before that transition finishes reflows the icon dock while the canvas is
// still moving, which reads as a shiver. Keep the full plane clipped and
// faded while closing, then switch to the dock only after the rail is stable.
// The small handoff buffer covers the final paint frame on slower laptops.
const NAVIGATION_CONTENT_PHASE_DELAY_MS = 380;
// On expansion, mount the readable plane only once the rail has its usable
// final width. This avoids a second reflow during an account-context refresh.
const NAVIGATION_CONTENT_REVEAL_DELAY_MS = 380;
const REDUCED_MOTION_MEDIA_QUERY = "(prefers-reduced-motion: reduce)";

const normalizeText = (value) => String(value || "").trim().toLowerCase();

const safeJsonParse = (raw, fallback = null) => {
  try {
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const getLoggedUserFromSession = () => {
  return safeJsonParse(sessionStorage.getItem("user_token"), null);
};

const normalizeRecentRoute = (value) => {
  const route = String(value || "").trim();
  // Module routes are static product paths. Reject query/hash/value-bearing
  // URLs so a deep-linked customer record can never reach session storage.
  return /^\/[A-Za-z0-9/_-]*$/.test(route) ? route.replace(/\/+$/, "") || "/" : "";
};

const readRecentModuleRoutes = () => {
  try {
    const stored = safeJsonParse(sessionStorage.getItem(RECENT_WORK_STORAGE_KEY), []);
    if (!Array.isArray(stored)) return [];

    return [...new Set(
      stored
        .map(normalizeRecentRoute)
        .filter(Boolean)
    )].slice(0, RECENT_WORK_LIMIT);
  } catch {
    return [];
  }
};

const persistRecentModuleRoutes = (moduleRoutes) => {
  try {
    sessionStorage.setItem(
      RECENT_WORK_STORAGE_KEY,
      JSON.stringify((moduleRoutes || []).slice(0, RECENT_WORK_LIMIT))
    );
  } catch {
    // A private browser session may block storage. Navigation still works;
    // recent work simply remains in memory for that session.
  }
};

const isDarkTheme = (theme) => {
  if (theme === true) return true;
  const value = String(theme || "").toLowerCase();
  return value.includes("dark") || value === "1";
};

const getInitialCollapsed = () => {
  try {
    return localStorage.getItem(SHELL_NAVIGATION_COLLAPSED_KEY) === "true";
  } catch {
    return false;
  }
};

const MODULE_ICON_MAP = {
  dashboard: DashboardIcon,
  "von-hub interface": HubRoundedIcon,
  "call center interface": ContactPageIcon,
  "customer search": PersonSearchIcon,
  "customer info 360": PersonSearchIcon,
  "calls info": QueryStatsIcon,
  "locations info": LocationOnRoundedIcon,
  "queue setup": HubRoundedIcon,
  "hubspot info": EmailIcon,
  "hubspot archive": ArchiveRoundedIcon,
  "access setup": ShieldRoundedIcon,
  "users setup": ManageAccountsRoundedIcon,
  "agents setup": SupportAgentRoundedIcon,
  "catalog setup": Inventory2RoundedIcon,
  "unified crm": AppsRoundedIcon,
  "email tickets": InboxRoundedIcon,
  "operational playbooks": FactCheckRoundedIcon,
  "automation studio": AutoAwesomeMotionRoundedIcon,
  reports: QueryStatsIcon,
  "quality assurance": FactCheckRoundedIcon,
  "audit trail": ShieldRoundedIcon,
  "account setup": BusinessRoundedIcon,
};

const mediaQueryMatches = (query) => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return Boolean(window.matchMedia(query).matches);
};

/** Keep presentation timing in sync if an operator resizes into the drawer
 * breakpoint or changes their reduced-motion preference while signed in. */
function useMediaQueryMatch(query) {
  const [matches, setMatches] = useState(() => mediaQueryMatches(query));

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const mediaQuery = window.matchMedia(query);
    const update = (event) => setMatches(Boolean(event.matches));
    setMatches(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", update);
      return () => mediaQuery.removeEventListener("change", update);
    }

    // Safari versions before EventTarget support use the legacy listener API.
    mediaQuery.addListener?.(update);
    return () => mediaQuery.removeListener?.(update);
  }, [query]);

  return matches;
}

// Membership workspaces use this source-controlled catalogue instead of the
// legacy global module collection. Visibility comes from canonical permission
// prefixes, while tenantReady controls whether the route may mount its data UI.
const TENANT_PRODUCT_MODULES = Object.freeze([
  { id: "dashboard", name: "Dashboard", route: "/Dashboard", permissions: ["dashboard"], tenantReady: true },
  { id: "call-center", name: "Call Center Interface", route: "/CallCenterInterface", permissions: ["call_center"], tenantReady: true },
  { id: "vonage-bridge", name: "Von-Hub Interface", route: "/Von-HubInterface", permissions: ["vonage_bridge"], tenantReady: false, integrationRequired: "Vonage" },
  { id: "customers", name: "Customer Search", route: "/CustomerSearch", permissions: ["customers"], tenantReady: true },
  { id: "calls", name: "Calls Info", route: "/CallsInfo", permissions: ["calls"], tenantReady: true },
  { id: "locations", name: "Locations Info", route: "/LocationsInfo", permissions: ["locations"], tenantReady: true },
  { id: "queues", name: "Queue Setup", route: "/QueueSetup", permissions: ["queues"], tenantReady: true },
  { id: "hubspot-email", name: "HubSpot Info", route: "/HubSpotInfo", permissions: ["hubspot_email"], tenantReady: false, integrationRequired: "HubSpot" },
  { id: "members", name: "Users Setup", route: "/UsersSetup", permissions: ["account.memberships", "iam.roles.assign"], tenantReady: true },
  { id: "access-management", name: "Access Setup", route: "/AccessSetup", permissions: ["iam"], tenantReady: true },
  { id: "routing-agents", name: "Agents Setup", route: "/AgentsSetup", permissions: ["routing_agents"], tenantReady: true },
  { id: "catalog", name: "Catalog Setup", route: "/CatalogSetup", permissions: ["catalog"], tenantReady: true },
  { id: "customer-360", name: "Customer Info 360", route: "/CustomerInfo360", permissions: ["customer_360"], tenantReady: true },
  { id: "cases", name: "Unified CRM", route: "/UnifiedCRM", permissions: ["cases"], tenantReady: true },
  { id: "email-tickets", name: "Email Tickets", route: "/EmailTickets", permissions: ["email_tickets"], tenantReady: true },
  { id: "operational-playbooks", name: "Operational Playbooks", route: "/OperationalPlaybooks", permissions: ["email_tickets.read"], tenantReady: true },
  { id: "automation-studio", name: "Automation Studio", route: "/AutomationStudio", permissions: ["email_tickets.read"], tenantReady: true },
  { id: "reports", name: "Reports", route: "/Reports", permissions: ["reports", "audit.operational"], tenantReady: true },
  { id: "quality-assurance", name: "Quality Assurance", route: "/QualityAssurance", permissions: ["email_tickets.read"], tenantReady: true },
  { id: "action-center", name: "Action Center", route: "/ActionCenter", permissions: ["email_tickets.read"], tenantReady: true },
  { id: "audit", name: "Audit Trail", route: "/Audit", permissions: ["audit.operational"], tenantReady: true },
  { id: "hubspot-archive", name: "HubSpot Archive", route: "/HubSpotArchive", permissions: ["hubspot_archive"], tenantReady: false, integrationRequired: "HubSpot archive" },
  { id: "account-setup", name: "Account Setup", route: "/AccountSetup", permissions: [], tenantReady: true },
]);

const getIconComponent = (moduleName) => {
  const name = normalizeText(moduleName);

  if (MODULE_ICON_MAP[name]) return MODULE_ICON_MAP[name];
  if (name.includes("email") || name.includes("ticket")) return EmailIcon;
  if (name.includes("hubspot")) return EmailIcon;
  if (name.includes("user")) return ManageAccountsRoundedIcon;
  if (name.includes("agent")) return SupportAgentRoundedIcon;
  if (name.includes("access") || name.includes("role") || name.includes("permission")) return ShieldRoundedIcon;
  if (name.includes("catalog") || name.includes("setup")) return TuneRoundedIcon;
  if (name.includes("location")) return LocationOnRoundedIcon;
  if (name.includes("call") || name.includes("contact")) return ContactPageIcon;
  if (name.includes("search")) return PersonSearchIcon;
  if (name.includes("report") || name.includes("stats") || name.includes("analytics")) return QueryStatsIcon;

  return DashboardIcon;
};

const getModuleMeta = (moduleName) => {
  const name = normalizeText(moduleName);

  if (name === "dashboard") {
    return {
      category: "Overview",
      description: "Operational overview",
      priority: 0,
    };
  }

  if (name === "account setup") {
    return {
      category: "Administration",
      description: "Customer account, people and connected channels",
      priority: 30,
    };
  }

  if (name.includes("email tickets")) {
    return {
      category: "Customer Care",
      description: "Email ticketing operations",
      priority: 10,
    };
  }

  if (name.includes("operational playbooks") || name.includes("service playbooks")) {
    return {
      category: "Customer Care",
      description: "Human-led guides for high-stakes service work",
      priority: 11,
    };
  }

  if (name.includes("hubspot archive")) {
    return {
      category: "Customer Care",
      description: "Archived HubSpot records",
      priority: 12,
    };
  }

  if (name.includes("hubspot")) {
    return {
      category: "Customer Care",
      description: "HubSpot email visibility",
      priority: 11,
    };
  }

  if (name.includes("unified crm")) {
    return {
      category: "Operations",
      description: "Cases, queues, search",
      priority: 20,
    };
  }

  if (name.includes("call center")) {
    return {
      category: "Operations",
      description: "Call center workflow",
      priority: 21,
    };
  }

  if (name.includes("von")) {
    return {
      category: "Operations",
      description: "Vonage interface",
      priority: 22,
    };
  }

  if (name.includes("customer search")) {
    return {
      category: "Operations",
      description: "Customer lookup",
      priority: 23,
    };
  }

  if (name.includes("calls info")) {
    return {
      category: "Operations",
      description: "Call analytics",
      priority: 24,
    };
  }

  if (name.includes("location")) {
    return {
      category: "Administration",
      description: "Location management",
      priority: 31,
    };
  }

  if (name === "reports" || name.includes("report center")) {
    return {
      category: "Insights",
      description: "Agent performance, audit and controlled exports",
      priority: 5,
    };
  }

  if (name === "quality assurance" || name.includes("quality workspace")) {
    return {
      category: "Insights",
      description: "Calibrated ticket reviews and coaching follow-through",
      priority: 6,
    };
  }

  if (name === "audit trail" || name === "audit") {
    return {
      category: "Insights",
      description: "Verified operational activity",
      priority: 7,
    };
  }

  if (name.includes("customer info 360") || name.includes("customer 360")) {
    return {
      category: "Customer Care",
      description: "Customer profile and activity timeline",
      priority: 13,
    };
  }

  if (name.includes("queue")) {
    return {
      category: "Administration",
      description: "Work queue policy",
      priority: 32,
    };
  }

  if (name.includes("access")) {
    return {
      category: "Administration",
      description: "Roles and permissions",
      priority: 33,
    };
  }

  if (name.includes("users")) {
    return {
      category: "Administration",
      description: "User management",
      priority: 34,
    };
  }

  if (name.includes("agents")) {
    return {
      category: "Administration",
      description: "Agent setup",
      priority: 35,
    };
  }

  if (name.includes("catalog")) {
    return {
      category: "Administration",
      description: "CRM dropdown catalog",
      priority: 36,
    };
  }

  return {
    category: "Modules",
    description: "CRM module",
    priority: 50,
  };
};

const CATEGORY_ORDER = {
  Overview: 0,
  Insights: 1,
  "Customer Care": 2,
  Operations: 3,
  Administration: 4,
  Modules: 5,
};

const sortModules = (items) => {
  return [...(items || [])].sort((a, b) => {
    const aName = normalizeText(a?.name);
    const bName = normalizeText(b?.name);

    if (aName === "dashboard" && bName !== "dashboard") return -1;
    if (aName !== "dashboard" && bName === "dashboard") return 1;

    const aCat = CATEGORY_ORDER[a?.category] ?? 99;
    const bCat = CATEGORY_ORDER[b?.category] ?? 99;
    if (aCat !== bCat) return aCat - bCat;

    const aPriority = Number(a?.priority ?? 99);
    const bPriority = Number(b?.priority ?? 99);
    if (aPriority !== bPriority) return aPriority - bPriority;

    return String(a?.name || "").localeCompare(String(b?.name || ""));
  });
};

const dedupeModules = (modules) => {
  const seen = new Set();
  const out = [];

  for (const item of modules || []) {
    const key = normalizeText(item?.name);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }

  return out;
};

const canAccessModule = (moduleName, role, access) => {
  const name = String(moduleName || "").trim();
  const normalizedRole = normalizeText(role);

  if (!name) return false;
  if (normalizeText(name) === "dashboard") return true;
  if (normalizedRole === "superadmin" || normalizedRole === "super admin") return true;
  if (access === "ALL") return true;

  if (access && typeof access === "object") {
    const direct = access[name];
    const directLower = access[normalizeText(name)];
    const perms = direct || directLower;
    return perms?.view === 1 || perms?.edit === 1 || perms?.view === true || perms?.edit === true;
  }

  return false;
};

const accountPermissionCodes = (access) => {
  if (Array.isArray(access)) {
    return access.map((permission) => normalizeText(permission)).filter(Boolean);
  }
  if (!access || typeof access !== "object") return [];
  return Object.entries(access)
    .filter(([, granted]) => granted === true || granted === 1)
    .map(([permission]) => normalizeText(permission));
};

const hasAccountControlPermission = (access) =>
  accountPermissionCodes(access).some(
    (permission) =>
      permission === "tenant.admin" ||
      permission === "account.settings.write" ||
      permission.startsWith("account.memberships.") ||
      permission.startsWith("account.invitations.") ||
      permission.startsWith("connections.email.")
  );

const hasTenantModulePermission = (module, access) => {
  const permissionCodes = accountPermissionCodes(access);
  if (access === "ALL" || permissionCodes.includes("tenant.admin")) return true;
  if (!module.permissions.length) return true;
  return module.permissions.some((prefix) =>
    permissionCodes.some(
      (permission) => permission === prefix || permission.startsWith(`${prefix}.`)
    )
  );
};

const canAccessAccountSetup = (role, access, authorizationSource = "") => {
  const membershipBacked = normalizeText(authorizationSource) === "tenant_membership";

  // Every validated membership can view its workspace control plane. Mutation
  // controls still come from server authority. Role *names* never elevate a
  // membership-backed session; only the actual permission codes above do.
  if (membershipBacked) return true;
  if (hasAccountControlPermission(access)) return true;
  const normalizedRole = normalizeText(role);
  if (access === "ALL") return true;
  // This is the explicitly fenced compatibility bridge for the one legacy
  // workspace while its module permissions remain role-shaped.
  return ["superadmin", "super admin", "admin", "administrator"].includes(
    normalizedRole
  );
};

function SidebarSkeleton({ collapsed }) {
  return (
    <div className="sidebar-skeleton-list" aria-hidden="true">
      {Array.from({ length: 7 }).map((_, index) => (
        <div className="sidebar-skeleton-item" key={index}>
          <span className="sidebar-skeleton-icon" />
          {!collapsed && (
            <span className="sidebar-skeleton-copy">
              <span />
              <span />
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Sidebar({
  theme,
  collapsed: controlledCollapsed,
  onCollapsedChange,
  onNavigate,
  shellControlsMobileFocus = false,
}) {
  const sidebarRef = useRef(null);
  const searchInputRef = useRef(null);
  const pendingSearchFocusRef = useRef(false);
  const mobileDrawerOpenerRef = useRef(null);
  const { t, moduleLabel, copy } = useLanguage();
  const location = useLocation();

  const [internalCollapsed, setInternalCollapsed] = useState(getInitialCollapsed);
  const [search, setSearch] = useState("");
  const [menuItems, setMenuItems] = useState([]);
  const [tab, setTab] = useState("all");
  const [recentModuleRoutes, setRecentModuleRoutes] = useState(readRecentModuleRoutes);
  const [loading, setLoading] = useState(true);
  const [moduleError, setModuleError] = useState("");
  const [savingFavouriteId, setSavingFavouriteId] = useState(null);
  const moduleLoadGenerationRef = useRef(0);
  const moduleLoadAbortControllerRef = useRef(null);

  const [loggedUser, setLoggedUser] = useState(getLoggedUserFromSession);

  useEffect(() => {
    const refreshSessionUser = () => {
      // Invalidate an in-flight catalogue request before the next account
      // render. A late response from the previous workspace must never flash
      // its module list into the newly selected account.
      moduleLoadGenerationRef.current += 1;
      moduleLoadAbortControllerRef.current?.abort?.();
      // Do not keep the previous account's product catalogue painted until
      // React has scheduled the new account's load. Besides looking like the
      // rail briefly belongs to the wrong workspace, that stale visual state
      // makes an account switch feel like the sidebar is flickering. The
      // fixed-height skeleton preserves the rail geometry while only the
      // newly authorized catalogue is being resolved.
      setMenuItems([]);
      setModuleError("");
      setLoading(true);
      setSearch("");
      setTab("all");
      setLoggedUser(getLoggedUserFromSession());
    };
    window.addEventListener(SESSION_CHANGED_EVENT, refreshSessionUser);
    return () => window.removeEventListener(SESSION_CHANGED_EVENT, refreshSessionUser);
  }, []);

  useEffect(() => () => {
    moduleLoadAbortControllerRef.current?.abort?.();
  }, []);

  const userEmail = String(
    loggedUser?.userEmail || loggedUser?.email || loggedUser?.username || ""
  ).trim();

  const role = String(
    loggedUser?.role || loggedUser?.userRole || loggedUser?.accessRole || "User"
  ).trim();

  const access = useMemo(
    () => loggedUser?.access || loggedUser?.permissions || {},
    [loggedUser]
  );
  const tenantAuthorizationSource = normalizeText(
    loggedUser?.tenantAuthorizationSource
  );
  const membershipBacked = tenantAuthorizationSource === "tenant_membership";
  const isControlled = typeof controlledCollapsed === "boolean";
  const collapsed = isControlled ? controlledCollapsed : internalCollapsed;
  const drawerViewport = useMediaQueryMatch(SHELL_DRAWER_MEDIA_QUERY);
  const reducedMotion = useMediaQueryMatch(REDUCED_MOTION_MEDIA_QUERY);
  const mobileDrawerOpen = drawerViewport && !collapsed;
  const [presentationCollapsed, setPresentationCollapsed] = useState(
    () => Boolean(
      typeof controlledCollapsed === "boolean"
        ? controlledCollapsed
      : getInitialCollapsed()
    )
  );
  const [contentTransition, setContentTransition] = useState(
    () => (collapsed ? "collapsed" : "expanded")
  );
  const presentationInitializedRef = useRef(false);
  const setCollapsed = useCallback(
    (nextValue) => {
      const next =
        typeof nextValue === "function" ? nextValue(collapsed) : Boolean(nextValue);
      if (isControlled) {
        onCollapsedChange?.(next);
      } else {
        setInternalCollapsed(next);
      }
    },
    [collapsed, isControlled, onCollapsedChange]
  );

  useLayoutEffect(() => {
    // Initial render has no preceding rail movement. Start in its final
    // presentation state so a fresh page never briefly hides navigation copy.
    if (!presentationInitializedRef.current) {
      presentationInitializedRef.current = true;
      setPresentationCollapsed(collapsed);
      setContentTransition(collapsed ? "collapsed" : "expanded");
      return undefined;
    }

    // A mobile rail is a drawer, not a compact desktop control. Its complete
    // labels must be ready while the drawer slides in. The same direct path is
    // used for people who explicitly request reduced motion.
    if (drawerViewport || reducedMotion) {
      setPresentationCollapsed(collapsed);
      setContentTransition(collapsed ? "collapsed" : "expanded");
      return undefined;
    }

    if (collapsed) {
      // Fade labels while the shell starts narrowing. The compact icon rail
      // then replaces them after the readable content has exited, rather than
      // removing it in the first frame of the width transition.
      setContentTransition("collapsing");
      const timer = setTimeout(() => {
        setPresentationCollapsed(true);
        setContentTransition("collapsed");
      }, NAVIGATION_CONTENT_PHASE_DELAY_MS);

      return () => clearTimeout(timer);
    }

    // Keep the compact dock mounted while the shell gains usable width. The
    // readable navigation is then mounted for the final label-fade window,
    // preventing a second layout change at the beginning of the motion.
    setContentTransition("expanding");
    let revealFrame = null;
    const timer = setTimeout(() => {
      setPresentationCollapsed(false);
      // Let the hidden expansion state paint once before the label transition
      // starts. requestAnimationFrame also cancels cleanly during rapid
      // keyboard toggles.
      if (typeof window.requestAnimationFrame === "function") {
        revealFrame = window.requestAnimationFrame(() => setContentTransition("expanded"));
      } else {
        revealFrame = setTimeout(() => setContentTransition("expanded"), 16);
      }
    }, NAVIGATION_CONTENT_REVEAL_DELAY_MS);

    return () => {
      clearTimeout(timer);
      if (revealFrame !== null) {
        if (typeof window.cancelAnimationFrame === "function") {
          window.cancelAnimationFrame(revealFrame);
        } else {
          clearTimeout(revealFrame);
        }
      }
    };
  }, [collapsed, drawerViewport, reducedMotion]);

  useEffect(() => {
    // The desktop compact rail keeps its readable plane unmounted until the
    // width transition has settled. Queue search focus until that input
    // actually exists; the previous fixed 120ms timeout fired while the
    // compact dock was still on screen, so Ctrl+K could expand the rail
    // without placing the operator in the search field.
    if (!pendingSearchFocusRef.current || collapsed || presentationCollapsed) {
      return undefined;
    }

    pendingSearchFocusRef.current = false;
    const focusSearch = () => searchInputRef.current?.focus({ preventScroll: true });
    const focusFrame = typeof window.requestAnimationFrame === "function"
      ? window.requestAnimationFrame(focusSearch)
      : window.setTimeout(focusSearch, 0);

    return () => {
      if (typeof window.cancelAnimationFrame === "function") {
        window.cancelAnimationFrame(focusFrame);
      } else {
        window.clearTimeout(focusFrame);
      }
    };
  }, [collapsed, presentationCollapsed]);

  useEffect(() => {
    if (!mobileDrawerOpen || shellControlsMobileFocus) return undefined;

    // On narrow screens the rail is a modal drawer. Keep keyboard focus in
    // the drawer while it is open and restore the invoking top-bar control
    // when it closes. This matches the visible backdrop and prevents a Tab
    // sequence from reaching controls hidden behind it.
    mobileDrawerOpenerRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;

    const focusSearch = () => searchInputRef.current?.focus({ preventScroll: true });
    const focusFrame = typeof window.requestAnimationFrame === "function"
      ? window.requestAnimationFrame(focusSearch)
      : window.setTimeout(focusSearch, 0);
    const getFocusableDrawerElements = () => {
      const drawer = sidebarRef.current;
      if (!drawer) return [];
      return Array.from(drawer.querySelectorAll(
        'a[href], button:not([disabled]):not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter((element) => (
        element instanceof HTMLElement
        && !element.hidden
        && element.getAttribute("aria-hidden") !== "true"
        && element.tabIndex >= 0
      ));
    };
    const trapDrawerFocus = (event) => {
      if (event.key !== "Tab") return;
      const focusable = getFocusableDrawerElements();
      if (!focusable.length) {
        event.preventDefault();
        return;
      }
      const activeIndex = focusable.indexOf(document.activeElement);
      if (event.shiftKey && activeIndex <= 0) {
        event.preventDefault();
        focusable.at(-1)?.focus();
      } else if (!event.shiftKey && (activeIndex === -1 || activeIndex === focusable.length - 1)) {
        event.preventDefault();
        focusable[0]?.focus();
      }
    };

    document.addEventListener("keydown", trapDrawerFocus, true);
    return () => {
      document.removeEventListener("keydown", trapDrawerFocus, true);
      if (typeof window.cancelAnimationFrame === "function") {
        window.cancelAnimationFrame(focusFrame);
      } else {
        window.clearTimeout(focusFrame);
      }
      const opener = mobileDrawerOpenerRef.current;
      if (opener?.isConnected) opener.focus({ preventScroll: true });
      mobileDrawerOpenerRef.current = null;
    };
  }, [mobileDrawerOpen, shellControlsMobileFocus]);

  const effectiveTheme = isDarkTheme(theme)
    ? "dark"
    : "light";
  const workspaceDisplayName = useMemo(
    () => resolveWorkspaceDisplayName(loggedUser),
    [loggedUser]
  );

  useEffect(() => {
    if (!ALLOWED_TABS.includes(tab)) {
      setTab("all");
    }
  }, [tab]);

  const loadModules = useCallback(async () => {
    if (!loggedUser) return;

    const loadGeneration = moduleLoadGenerationRef.current + 1;
    moduleLoadGenerationRef.current = loadGeneration;
    const isCurrentLoad = () => moduleLoadGenerationRef.current === loadGeneration;
    moduleLoadAbortControllerRef.current?.abort?.();
    const abortController = typeof AbortController === "function"
      ? new AbortController()
      : null;
    moduleLoadAbortControllerRef.current = abortController;

    try {
      setLoading(true);
      setModuleError("");

      // Legacy module metadata is global. Do not even request it after a real
      // tenant membership becomes the active security boundary.
      let rawModules = [];
      if (!membershipBacked) {
        const response = await apiClient.get("/get_modules", {
          timeout: 20000,
          ...(abortController ? { signal: abortController.signal } : {}),
        });
        rawModules = Array.isArray(response.data) ? response.data : [];
      }

      const withDashboard = membershipBacked
        ? []
        : rawModules.some((m) => normalizeText(m?.name) === "dashboard")
        ? rawModules
        : [{ _id: "dashboard", name: "Dashboard", favourite: {}, _synthetic: true }, ...rawModules];

      // Show every module authorized by the membership. Routes that have not
      // completed tenant isolation remain visible with a rollout marker and
      // are still contained by TenantRolloutBoundary in App.js.
      const tenantModules = membershipBacked
        ? TENANT_PRODUCT_MODULES
            .filter((module) => !isTemporarilyHiddenModule(module))
            .filter((module) => hasTenantModulePermission(module, access))
            .map((module) => ({
              _id: module.id,
              name: module.name,
              route: module.route,
              favourite: {},
              _synthetic: true,
              _tenantReady: module.tenantReady,
              _integrationRequired: module.integrationRequired || "",
            }))
        : [];
      const withTenantModules = [...withDashboard, ...tenantModules];

      // Account Setup is a product control-plane route, not a legacy module DB
      // row. Its visibility is a convenience only; the API remains authoritative.
      const withAccountSetup = canAccessAccountSetup(
        role,
        access,
        tenantAuthorizationSource
      )
        && !withTenantModules.some((m) => normalizeText(m?.name) === "account setup")
        ? [
            ...withTenantModules,
            {
              _id: "account-setup",
              name: "Account Setup",
              favourite: {},
              _synthetic: true,
              _accountControl: true,
              _tenantReady: true,
            },
          ]
        : withTenantModules;

      const modules = dedupeModules(withAccountSetup)
        .filter((module) => !isTemporarilyHiddenModule(module))
        .map((m, idx) => {
          const name = String(m?.name || "").trim();
          const meta = getModuleMeta(name);
          const Icon = getIconComponent(name);

          return {
            id: String(m?._id || name || idx),
            name,
            Icon,
            route: resolveModuleRoute(name, m?.route),
            favourite: m?.favourite && typeof m.favourite === "object" ? m.favourite : {},
            canFavourite: !m?._synthetic,
            rolloutPending: membershipBacked && m?._tenantReady !== true,
            rolloutLabel: m?._integrationRequired ? "Setup required" : "Migrating",
            category: meta.category,
            description: meta.description,
            priority: meta.priority,
          };
        })
        .filter((module) => module.route && (
          membershipBacked
            ? true
            : normalizeText(module.name) === "account setup"
            ? canAccessAccountSetup(role, access, tenantAuthorizationSource)
            : canAccessModule(module.name, role, access)
        ));

      if (!isCurrentLoad()) return;
      setMenuItems(sortModules(modules));
    } catch (err) {
      if (!isCurrentLoad()) return;
      console.error("MODULE LOAD ERROR:", err);
      setMenuItems([]);
      setModuleError(err?.response?.data?.message || "Could not load CRM modules.");
    } finally {
      if (isCurrentLoad()) setLoading(false);
      if (moduleLoadAbortControllerRef.current === abortController) {
        moduleLoadAbortControllerRef.current = null;
      }
    }
  }, [loggedUser, role, access, membershipBacked, tenantAuthorizationSource]);

  useEffect(() => {
    loadModules();
  }, [loadModules]);

  const favouriteCount = useMemo(
    () => menuItems.filter((m) => Boolean(m.favourite?.[userEmail])).length,
    [menuItems, userEmail]
  );

  const recentItems = useMemo(() => {
    const itemsByRoute = new Map(
      menuItems.map((item) => [normalizeRecentRoute(item.route), item])
    );

    // A route can be removed from a role or hidden during a rollout. Keep the
    // session history harmless by showing only modules currently authorized
    // for this workspace.
    return recentModuleRoutes
      .map((route) => itemsByRoute.get(route))
      .filter(Boolean);
  }, [menuItems, recentModuleRoutes]);

  const recentCount = recentItems.length;

  useEffect(() => {
    if (tab === "fav" && favouriteCount === 0) setTab("all");
    if (tab === "recent" && recentCount === 0) setTab("all");
  }, [favouriteCount, recentCount, tab]);

  const recordRecentModule = useCallback((route) => {
    const normalizedRoute = normalizeRecentRoute(route);
    if (!normalizedRoute) return;

    setRecentModuleRoutes((current) => {
      const next = [normalizedRoute, ...current.filter((item) => item !== normalizedRoute)]
        .slice(0, RECENT_WORK_LIMIT);
      persistRecentModuleRoutes(next);
      return next;
    });
  }, []);

  // Deep links and browser navigation should feel like intentional work too,
  // not only clicks made inside this rail. Route matching stays local to the
  // authorized module catalogue; no URL, record ID, query or state is saved.
  useEffect(() => {
    const currentPath = String(location.pathname || "").replace(/\/+$/, "") || "/";
    const currentModule = menuItems.find((item) => {
      const modulePath = String(item.route || "").replace(/\/+$/, "") || "/";
      return currentPath === modulePath || currentPath.startsWith(`${modulePath}/`);
    });
    if (currentModule) recordRecentModule(currentModule.route);
  }, [location.pathname, menuItems, recordRecentModule]);

  const visibleItems = useMemo(() => {
    let result = tab === "recent" ? [...recentItems] : [...menuItems];

    if (tab === "fav") {
      result = result.filter((m) => Boolean(m.favourite?.[userEmail]));
    }

    const q = normalizeText(search);
    if (q) {
      result = result.filter((m) => {
        return [m.name, moduleLabel(m.name), m.category, copy(m.category), m.description, copy(m.description)]
          .join(" ")
          .toLowerCase()
          .includes(q);
      });
    }

    // Recent work is deliberately ordered by last visit rather than category
    // priority, giving operators a useful continuation path after a context
    // switch. All other tabs retain the stable product ordering.
    return tab === "recent" ? result : sortModules(result);
  }, [copy, menuItems, moduleLabel, recentItems, tab, search, userEmail]);

  const groupedItems = useMemo(() => {
    const groups = [];
    const map = new Map();

    for (const item of visibleItems) {
      const key = item.category || "Modules";
      if (!map.has(key)) {
        const bucket = { name: key, items: [] };
        map.set(key, bucket);
        groups.push(bucket);
      }
      map.get(key).items.push(item);
    }

    return tab === "recent"
      ? [{ name: t("sidebar.recentWork", {}, "Recent work"), items: visibleItems }]
      : groups.sort((a, b) => (CATEGORY_ORDER[a.name] ?? 99) - (CATEGORY_ORDER[b.name] ?? 99));
  }, [t, tab, visibleItems]);

  const tabItems = useMemo(
    () => {
      const items = [
        { id: "all", label: t("sidebar.all"), count: menuItems.length },
      ];
      if (recentCount > 0) {
        items.push({ id: "recent", label: t("sidebar.recent", {}, "Recent"), count: recentCount });
      }
      if (!membershipBacked && favouriteCount > 0) {
        items.push({ id: "fav", label: t("sidebar.favourites"), count: favouriteCount });
      }
      return items;
    },
    [favouriteCount, membershipBacked, menuItems.length, recentCount, t]
  );

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => !prev);
  }, [setCollapsed]);

  const expandAndFocusSearch = useCallback(() => {
    if (!collapsed && !presentationCollapsed) {
      const focusSearch = () => searchInputRef.current?.focus({ preventScroll: true });
      if (typeof window.requestAnimationFrame === "function") {
        window.requestAnimationFrame(focusSearch);
      } else {
        window.setTimeout(focusSearch, 0);
      }
      return;
    }
    pendingSearchFocusRef.current = true;
    setCollapsed(false);
  }, [collapsed, presentationCollapsed, setCollapsed]);

  const openRecentWork = useCallback(() => {
    if (!recentCount) return;
    setSearch("");
    setTab("recent");
    setCollapsed(false);
  }, [recentCount, setCollapsed]);

  const handleClearSearch = useCallback(() => {
    setSearch("");
    searchInputRef.current?.focus();
  }, []);

  const toggleFavourite = useCallback(
    async (event, item) => {
      event.preventDefault();
      event.stopPropagation();

      if (!userEmail || !item?.id || !item?.canFavourite || savingFavouriteId) return;

      const previous = menuItems;
      const current = Boolean(item.favourite?.[userEmail]);
      const nextFavourite = {
        ...(item.favourite || {}),
        [userEmail]: !current,
      };

      const updated = menuItems.map((module) =>
        String(module.id) === String(item.id)
          ? {
              ...module,
              favourite: nextFavourite,
            }
          : module
      );

      setSavingFavouriteId(String(item.id));
      setMenuItems(sortModules(updated));

      try {
        await apiClient.put(
          `/update_favourite/${encodeURIComponent(item.id)}`,
          { favourite: !current },
          { timeout: 15000 }
        );
      } catch (err) {
        console.error("FAVOURITE UPDATE ERROR:", err);
        setMenuItems(previous);
      } finally {
        setSavingFavouriteId(null);
      }
    },
    [menuItems, userEmail, savingFavouriteId]
  );

  useEffect(() => {
    const onKeyDown = (event) => {
      const tag = String(event.target?.tagName || "").toLowerCase();
      const isTyping = tag === "input" || tag === "textarea" || tag === "select" || event.target?.isContentEditable;

      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        expandAndFocusSearch();
        return;
      }

      if (!isTyping && event.altKey && event.key.toLowerCase() === "b") {
        event.preventDefault();
        toggleCollapsed();
      }

      if (
        event.key === "Escape" &&
        !collapsed &&
        window.matchMedia?.(SHELL_DRAWER_MEDIA_QUERY)?.matches
      ) {
        event.preventDefault();
        setCollapsed(true);
        return;
      }

      if (event.key === "Escape" && search) {
        setSearch("");
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [collapsed, expandAndFocusSearch, search, setCollapsed, toggleCollapsed]);

  if (!loggedUser) return null;

  return (
    <aside
      ref={sidebarRef}
      id="crm-primary-navigation"
      className={`sidebar ${collapsed ? "collapsed" : ""}`}
      data-theme={effectiveTheme}
      data-collapsed={collapsed ? "true" : "false"}
      data-content-collapsed={presentationCollapsed ? "true" : "false"}
      data-content-transition={contentTransition}
      data-loading={loading ? "true" : "false"}
      aria-label={t("sidebar.crmNavigation")}
      aria-busy={loading || undefined}
      role={mobileDrawerOpen ? "dialog" : undefined}
      aria-modal={mobileDrawerOpen || undefined}
    >
      <div className="sidebar-inner">
        <button
          type="button"
          className="sidebar-rail-toggle"
          onClick={toggleCollapsed}
          aria-label={collapsed ? t("sidebar.expandNavigation") : t("sidebar.collapseNavigation")}
          aria-keyshortcuts="Alt+B"
          tabIndex={drawerViewport ? -1 : undefined}
          title={collapsed ? t("sidebar.expandNavigationShortcut") : t("sidebar.collapseNavigationShortcut")}
        >
          {collapsed ? (
            <KeyboardDoubleArrowRightRoundedIcon aria-hidden="true" />
          ) : (
            <KeyboardDoubleArrowLeftRoundedIcon aria-hidden="true" />
          )}
          {!presentationCollapsed && <span>{t("sidebar.collapseNavigation")}</span>}
        </button>

        <section
          className="sidebar-workspace-card"
          aria-label={`Active customer workspace: ${workspaceDisplayName}`}
          title={presentationCollapsed ? `${t("sidebar.activeWorkspace")}: ${workspaceDisplayName}` : undefined}
        >
          <span className="sidebar-workspace-card__icon" aria-hidden="true">
            <BusinessRoundedIcon />
          </span>
          {!presentationCollapsed && (
            <>
            <div className="sidebar-workspace-card__copy">
              <span>{t("sidebar.activeWorkspace")}</span>
              <strong title={workspaceDisplayName}>{workspaceDisplayName}</strong>
            </div>
            <span className="sidebar-workspace-card__role" title={role}>
              <i className="sidebar-workspace-card__pulse" aria-hidden="true" />
              {role}
            </span>
            <div className="sidebar-workspace-card__stats" aria-label={t("sidebar.availableModules", { count: menuItems.length })}>
              <span>
                <strong>{loading ? "…" : menuItems.length}</strong>
                <small>{t("sidebar.modules")}</small>
              </span>
              <i aria-hidden="true" />
              <span>
                <strong>{favouriteCount}</strong>
                <small>{t("sidebar.favourites")}</small>
              </span>
            </div>
            </>
          )}
        </section>

        <div className="sidebar-utilities">
          {!presentationCollapsed ? (
            <div className="sidebar-search-card">
              <SearchIcon className="sidebar-search-icon" />
              <input
                ref={searchInputRef}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="sidebar-search-input"
                placeholder={t("sidebar.searchModules")}
                aria-label={t("sidebar.searchCrmModules")}
                aria-keyshortcuts="Control+K"
              />
              {search ? (
                <button
                  type="button"
                  className="sidebar-search-clear"
                  onClick={handleClearSearch}
                  title={t("sidebar.clearSearch")}
                  aria-label={t("sidebar.clearSearch")}
                >
                  <ClearRoundedIcon />
                </button>
              ) : (
                <span className="sidebar-search-kbd" aria-hidden="true">Ctrl K</span>
              )}
            </div>
          ) : (
            <div
              className="sidebar-compact-actions"
              role="group"
              aria-label={t("sidebar.navigationQuickActions", {}, "Navigation quick actions")}
            >
              <button type="button" className="sidebar-search-collapsed" onClick={expandAndFocusSearch} title={t("sidebar.search")} aria-label={t("sidebar.search")}>
                <SearchIcon />
              </button>
              {recentCount > 0 && (
                <button
                  type="button"
                  className="sidebar-recent-collapsed"
                  onClick={openRecentWork}
                  title={t("sidebar.openRecentWork", { count: recentCount }, "Open recent work ({count})")}
                  aria-label={t("sidebar.openRecentWork", { count: recentCount }, "Open recent work ({count})")}
                >
                  <HistoryRoundedIcon />
                  <span aria-hidden="true">{recentCount}</span>
                </button>
              )}
            </div>
          )}

          {!presentationCollapsed && tabItems.length > 1 && (
            <div className={`sidebar-tabs sidebar-tabs--${tabItems.length}`} aria-label={t("sidebar.filters")}>
              {tabItems.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  className={`sidebar-tab ${tab === item.id ? "active" : ""}`}
                  onClick={() => setTab(item.id)}
                  aria-pressed={tab === item.id}
                >
                  <span>{item.label}</span>
                  <b>{item.count}</b>
                </button>
              ))}
            </div>
          )}
        </div>

        <nav
          className="sidebar-menu"
          aria-label={t("sidebar.searchCrmModules")}
          aria-busy={loading || undefined}
        >
          {!presentationCollapsed && (
            <div className="sidebar-menu-heading" aria-live="polite" aria-atomic="true">
              <span>{t("sidebar.modules")}</span>
              <b aria-label={loading ? t("sidebar.loadingModules") : t("sidebar.availableModules", { count: menuItems.length })}>
                {loading ? "..." : menuItems.length}
              </b>
            </div>
          )}
          {loading ? (
            <div role="status" aria-label={t("sidebar.loadingModules")}>
              <span className="crm-visually-hidden">{t("sidebar.loadingModules")}</span>
              <SidebarSkeleton collapsed={presentationCollapsed} />
            </div>
          ) : moduleError ? (
            <div className={`sidebar-state ${presentationCollapsed ? "is-collapsed" : ""}`} role="alert">
              <ErrorOutlineRoundedIcon />
              {!presentationCollapsed && (
                <>
                  <strong>{t("sidebar.modulesUnavailable")}</strong>
                  <span>{moduleError}</span>
                  <button type="button" onClick={loadModules}>{t("sidebar.tryAgain")}</button>
                </>
              )}
            </div>
          ) : groupedItems.length === 0 ? (
            <div className={`sidebar-state ${presentationCollapsed ? "is-collapsed" : ""}`}>
              <SearchIcon />
              {!presentationCollapsed && (
                <>
                  <strong>{t("sidebar.noModulesFound")}</strong>
                  <span>{search ? t("sidebar.tryClearingSearch") : t("sidebar.noModulesMatch")}</span>
                  {search && <button type="button" onClick={handleClearSearch}>{t("sidebar.clearSearch")}</button>}
                </>
              )}
            </div>
          ) : (
            groupedItems.map((group) => (
              <div
                className="sidebar-group"
                key={group.name}
                role="group"
                aria-label={presentationCollapsed ? copy(group.name) : undefined}
              >
                {!presentationCollapsed && (
                  <h2 className="sidebar-group-title">
                    <span>{copy(group.name)}</span>
                    <b aria-label={`${group.items.length} ${t("sidebar.modules")}`}>{group.items.length}</b>
                  </h2>
                )}

                {group.items.map((item) => {
                  const Icon = item.Icon || DashboardIcon;
                  const isFavourite = Boolean(item.favourite?.[userEmail]);
                  const isSaving = String(savingFavouriteId) === String(item.id);
                  const itemLabel = moduleLabel(item.name);
                  const itemDescription = copy(item.description);
                  const rolloutLabel = item.rolloutPending
                    ? (item.rolloutLabel === "Setup required"
                      ? t("sidebar.setupRequired")
                      : t("sidebar.migrating"))
                    : "";

                  return (
                    <div
                      className="sidebar-link-row"
                      key={item.id}
                    >
                      <NavLink
                        to={item.route}
                        data-label={itemLabel}
                        aria-label={`${itemLabel}${rolloutLabel ? `. ${rolloutLabel}` : ""}`}
                        className={({ isActive }) => `sidebar-link ${isActive ? "is-active" : ""} ${item.rolloutPending ? "has-rollout" : ""}`}
                        title={`${itemLabel}${itemDescription ? ` — ${itemDescription}` : ""}${rolloutLabel ? ` — ${rolloutLabel}` : ""}`}
                        onClick={(event) => {
                          recordRecentModule(item.route);
                          onNavigate?.(event);
                        }}
                      >
                        <span className="sidebar-link-icon">
                          <Icon />
                        </span>

                        {!presentationCollapsed && (
                          <span className="sidebar-link-copy">
                            <span className="sidebar-link-main">
                              <span className="sidebar-link-name">{itemLabel}</span>
                              {item.rolloutPending && <span className="sidebar-link-rollout">{rolloutLabel}</span>}
                            </span>
                            <span className="sidebar-link-description" aria-hidden="true">
                              {itemDescription}
                            </span>
                          </span>
                        )}
                      </NavLink>

                      {!presentationCollapsed && item.canFavourite && (
                        <button
                          type="button"
                          className={`sidebar-favourite ${isFavourite ? "is-on" : ""} ${isSaving ? "is-saving" : ""}`}
                          onClick={(event) => toggleFavourite(event, item)}
                          disabled={isSaving}
                          title={isFavourite ? t("sidebar.removeFavourite") : t("sidebar.addFavourite")}
                          aria-label={isFavourite ? t("sidebar.removeFavourite") : t("sidebar.addFavourite")}
                        >
                          {isFavourite ? <StarIcon /> : <StarBorderIcon />}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </nav>

      </div>
    </aside>
  );
}
